import { Check } from 'lucide-react';
import { groupQuestionnairesByTag } from '@/lib/questionnaireTags';
import { cn } from '@/utils/cn';

const ICON = 1.75;

export type JournalQuestionnaireRow = {
  id: string;
  name: string;
  tags?: readonly string[] | null;
};

/**
 * Tilladte checklist grouped by the tags stored on each questionnaire.
 *
 * A row with several tags is repeated under each tag (see
 * `groupQuestionnairesByTag`). The untagged label is shown only when the
 * visible list also has a tagged questionnaire, so a clinic that does not
 * use tags keeps the flat list.
 */
export function JournalQuestionnaireChecklist({
  rows,
  picked,
  onToggle,
  untaggedLabel,
}: {
  rows: readonly JournalQuestionnaireRow[];
  picked: readonly string[];
  onToggle: (id: string) => void;
  untaggedLabel: string;
}) {
  const sections = groupQuestionnairesByTag(rows);
  const showTagHeaders = sections.some((section) => section.tag != null);

  return (
    <div className="qp-tags mt-2" data-testid="journal-questionnaire-checklist">
      {sections.map((section) => {
        const label = section.tag ?? (showTagHeaders ? untaggedLabel : null);
        return (
          <section
            key={section.tag ?? '\u0000'}
            className="qp-tag"
            data-testid="journal-questionnaire-tag"
            data-tag={section.tag ?? ''}
            aria-label={label ?? undefined}
          >
            {label ? <p className="qp-tag-h">{label}</p> : null}
            <ul className="space-y-1">
              {section.items.map((row) => {
                const selected = picked.includes(row.id);
                return (
                  <li key={row.id}>
                    <button
                      type="button"
                      className={cn('qp-r', selected && 'on')}
                      onClick={() => onToggle(row.id)}
                    >
                      <span className="box" aria-hidden>
                        {selected ? <Check className="i xs" strokeWidth={ICON} /> : null}
                      </span>
                      <span>
                        <span className="nm">{row.name}</span>
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
          </section>
        );
      })}
    </div>
  );
}
